import { Media } from '@/components/Media'
import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { Button, Card, Icon, Meter, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { StatTile } from '@/components/StatTile'
import { PageHeader } from '@/app/layouts/PageHeader'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import { KINDS, VENUES, entryPrice, type WellnessKind } from './wellness-data'
import { CITIES } from './travel-data'
import { FilterBar, FilterSelect } from '@/components/FilterBar'
import { EmptyState } from '@/components/States'
import { formatMoney, fromMajor } from '@/lib/money'


const SORTS = [
  { value: 'recommended', label: 'Most booked' },
  { value: 'price-asc', label: 'Price: low to high' },
  { value: 'score', label: 'Guest score' },
]

const BUDGETS = [
  { value: 'any', label: 'Any price' },
  { value: '15', label: 'Under $15' },
  { value: '25', label: 'Under $25' },
  { value: '40', label: 'Under $40' },
]

export function WellnessScreen() {
  const [params, setParams] = useSearchParams()
  const kind = params.get('kind') as WellnessKind | null
  const city = params.get('city') ?? 'all'
  const [query, setQuery] = useState('')
  const [budget, setBudget] = useState('any')
  const [sort, setSort] = useState('recommended')

  /* Functional form: two selects changed in the same render must not clobber
     each other, which a snapshot of `params` would allow. */
  const setParam = (key: string, value: string) => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        if (value === 'all') next.delete(key)
        else next.set(key, value)
        return next
      },
      { replace: true },
    )
  }

  const dirty =
    Boolean(kind) || city !== 'all' || query !== '' || budget !== 'any' || sort !== 'recommended'

  const reset = () => {
    setQuery('')
    setBudget('any')
    setSort('recommended')
    setParams(new URLSearchParams(), { replace: true })
  }

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const cap = budget === 'any' ? Infinity : Number(budget)
    const filtered = VENUES.filter((venue) => {
      if (kind && venue.kind !== kind) return false
      if (city !== 'all' && venue.cityId !== city) return false
      if (entryPrice(venue) > cap) return false
      if (!needle) return true
      return (
        venue.name.toLowerCase().includes(needle) ||
        venue.city.toLowerCase().includes(needle) ||
        venue.detail.toLowerCase().includes(needle)
      )
    })
    const sorted = [...filtered]
    if (sort === 'price-asc') sorted.sort((a, b) => entryPrice(a) - entryPrice(b))
    if (sort === 'score') sorted.sort((a, b) => Number(b.score) - Number(a.score))
    return sorted
  }, [kind, city, query, budget, sort])

  return (
    <>
      <PageHeader
        title="Wellness"
        subtitle="Yoga, gyms and spas — where you are and back home. 5% back on every booking."
        actions={<Button variant="primary" size="md" iconStart="event" to="/transactions">My bookings</Button>}
      />

      <BentoGrid>
        <BentoSpan span={3}>
          <Card tone="dark">
            <Pill tone="accent">Featured · 5% back</Pill>
            <Text as="p" variant="heading" weight="bold" tone="onDark" className="pb-mt-4">
              Thermal circuit · Condesa
            </Text>
            <Text variant="caption" tone="onDarkMuted" className="pb-mt-2">
              Steam, sauna and cold plunge, two hours with no appointment. A quarter of what the
              same circuit costs in a US hotel spa.
            </Text>
            <Card.Footer>
              <Stack direction="row" align="center" gap={4} wrap>
                <Stack direction="row" align="baseline" gap={2}>
                  <Text variant="title" weight="bold" tone="accent" numeric>$24</Text>
                  <Text variant="caption" tone="onDarkMuted">vs $95 in the US</Text>
                </Stack>
                <Button size="md" iconEnd="arrow_forward" to="/wellness/bano-condesa">See times</Button>
              </Stack>
            </Card.Footer>
          </Card>
        </BentoSpan>

        <Stack gap={3}>
          <StatTile stat={{ label: 'Wellness spend 2026', value: '$1,284.00', note: '$64.20 earned back so far' }} />
          <Card tone="light">
            <Stack direction="row" justify="between" align="center">
              <Text variant="caption" weight="semibold">Wellness budget</Text>
              <Text variant="caption" tone="secondary" numeric>$114 left</Text>
            </Stack>
            <div className="pb-mt-3">
              <Meter value={86 / 200} label="Wellness budget used" />
            </div>
            <Text variant="eyebrow" tone="secondary" className="pb-mt-3">
              of $200 this month · renews 1 October
            </Text>
          </Card>
        </Stack>

        <BentoSpan span={2}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">What do you need?</Text>
            <Stack direction="row" gap={2} wrap className="pb-mt-4">
              {KINDS.map((category) => (
                <Card
                  key={category.id}
                  tone={category.id === kind ? 'dark' : 'plain'}
                  interactive
                  padded={false}
                  className="pb-cat"
                  as={Link}
                  to={category.id === kind ? '/wellness' : `/wellness?kind=${category.id}`}
                >
                  <Stack align="center" gap={2} style={{ padding: 'var(--pb-space-4)' }}>
                    <Icon name={category.icon} size={22} />
                    <Text
                      variant="caption"
                      weight="semibold"
                      tone={category.id === kind ? 'onDark' : 'primary'}
                    >
                      {category.label}
                    </Text>
                    <Text
                      variant="eyebrow"
                      tone={category.id === kind ? 'onDarkMuted' : 'secondary'}
                    >
                      {category.count}
                    </Text>
                  </Stack>
                </Card>
              ))}
            </Stack>
          </Card>
        </BentoSpan>

        <BentoSpan span={4}>
          <Card tone="light">
            <FilterBar
              searchLabel="Search studios, gyms and spas"
              searchValue={query}
              onSearch={setQuery}
              count={shown.length}
              noun="place"
              dirty={dirty}
              onReset={reset}
              selects={
                <>
                  <FilterSelect
                    label="City"
                    value={city}
                    onChange={(value) => setParam('city', value)}
                    options={[
                      { value: 'all', label: 'All corridors' },
                      ...CITIES.map((item) => ({ value: item.id, label: item.label })),
                    ]}
                  />
                  <FilterSelect
                    label="Type"
                    value={kind ?? 'all'}
                    onChange={(value) => setParam('kind', value)}
                    options={[
                      { value: 'all', label: 'Everything' },
                      ...KINDS.map((item) => ({ value: item.id, label: item.label })),
                    ]}
                  />
                  <FilterSelect label="Budget" value={budget} onChange={setBudget} options={BUDGETS} />
                  <FilterSelect label="Sort by" value={sort} onChange={setSort} options={SORTS} />
                </>
              }
            />

            <Stack gap={0} className="pb-mt-3">
              {shown.map((venue) => {
                const headline = venue.services[0]
                return (
                <Link key={venue.slug} to={`/wellness/${venue.slug}`} className={checkout.line} style={{ width: '100%' }}>
                  <Media src={venue.image} alt="" width={56} ratio={1} radius="md" icon="health_and_safety" />
                  <Stack grow gap={0} align="start">
                    <Text variant="caption" weight="semibold" truncate>{venue.name}</Text>
                    <Text variant="caption" tone="secondary" truncate>{venue.city} · {venue.detail}</Text>
                  </Stack>
                  <Pill tone="neutral">{venue.score}</Pill>
                  <Stack align="end" gap={0} style={{ width: '5.5rem' }}>
                    <Text variant="caption" weight="semibold" numeric>
                      {headline?.price === 0
                        ? 'Free'
                        : formatMoney(fromMajor(headline?.price ?? 0, 'USD'))}
                    </Text>
                    <Text variant="eyebrow" tone="secondary" truncate>{headline?.name}</Text>
                  </Stack>
                </Link>
                )
              })}
              {shown.length === 0 && (
                <EmptyState
                  icon="search_off"
                  title="Nothing here matches"
                  body="Try another city, or raise the budget."
                  action={{ label: 'Clear filters', onClick: reset }}
                />
              )}
            </Stack>
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
